import { DndContext, closestCenter } from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DEFAULT_TRANSLATE, DROP_ANIMATION_CONFIG } from './constants.js';
import { ListContainer } from './containers.js';
import Droppable from './Droppable.js';
import GenericDragContainer from './GenericDragContainer.js';
import { customRestrictToWindowEdges } from './helpers.js';
import { useDrag } from './hooks/useDrag.js';
import SortableContainer from './SortableContainer.js';
import { DRAG_ZINDEX } from './styles.js';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';

const DragContainer = ({
  activationConstraint,
  idArray = [],
  measuring,
  modifiers = [],
  removable,
  genericDragProps,
  children,
  reorderIds = arrayMove,
  setIdArray = () => {},
  collisionDetection = closestCenter,
  coordinateGetter = sortableKeyboardCoordinates,
  columns = 1,
  Container = ListContainer,
  dropAnimation = DROP_ANIMATION_CONFIG,
  handle = false,
  hideCopy = false,
  horizontal = false,
  style,
  useDragOverlay = true,
  adjustScale = false,
  renderItem,
  colorCoder = () => null,
  getItemStyles = () => ({}),
  getNewIndex,
  isDisabled = () => false,
  wrapperStyle = () => ({}),
  animateLayoutChanges,
  strategy = verticalListSortingStrategy
}) => {
  var _genericDragProps$def;
  const useGeneric = Boolean(genericDragProps);
  const {
    sensors,
    activeId,
    activeIndex,
    translate,
    initialTranslate,
    onDragStart,
    onDragEnd,
    onDragCancel,
    onDragMove,
    handleRemove
  } = useDrag({
    useGeneric,
    activationConstraint,
    idArray,
    removable,
    defaultTranslate: (_genericDragProps$def = genericDragProps === null || genericDragProps === void 0 ? void 0 : genericDragProps.defaultTranslate) !== null && _genericDragProps$def !== void 0 ? _genericDragProps$def : DEFAULT_TRANSLATE,
    coordinateGetter,
    setIdArray,
    reorderIds
  });
  const sortableProps = {
    columns,
    Container,
    dropAnimation,
    handle,
    hideCopy,
    horizontal,
    style,
    idArray,
    useDragOverlay,
    adjustScale,
    renderItem,
    activeId,
    activeIndex,
    handleRemove,
    colorCoder,
    getItemStyles,
    getNewIndex,
    isDisabled,
    wrapperStyle,
    animateLayoutChanges,
    strategy
  };
  const renderGeneric = () => jsxs(Fragment, {
    children: [jsx(GenericDragContainer, {
      id: genericDragProps.draggableId,
      translate: translate,
      children: children
    }), jsx(Droppable, {
      id: genericDragProps.droppableId,
      draggableId: genericDragProps.draggableId,
      zIndex: genericDragProps.draggableZIndex || DRAG_ZINDEX
    })]
  });
  const renderSortable = () => jsx(SortableContainer, {
    ...sortableProps
  });
  return jsx(DndContext, {
    sensors: sensors,
    measuring: measuring,
    modifiers: useGeneric ? [customRestrictToWindowEdges(initialTranslate, genericDragProps === null || genericDragProps === void 0 ? void 0 : genericDragProps.boundingRect), ...modifiers] : modifiers,
    collisionDetection: useGeneric ? undefined : collisionDetection,
    onDragStart: onDragStart,
    onDragEnd: onDragEnd,
    onDragCancel: onDragCancel,
    onDragMove: onDragMove,
    children: useGeneric ? renderGeneric() : renderSortable()
  });
};

export { DragContainer };
